import { isAxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '@/api/client'
import { captureException } from '@/domains/observability'

export type AddressPrediction = {
  placeId: string
  description: string
}

export type AddressPlaceDetails = {
  description: string
  countryCode: string | null
  country: string | null
  region: string | null
  latitude: string | null
  longitude: string | null
  /** the lookup itself failed, as opposed to google returning no country. */
  lookupFailed: boolean
}

type PlaceDetailsResponse = Omit<
  AddressPlaceDetails,
  'description' | 'lookupFailed'
>

function report(error: unknown) {
  captureException(error instanceof Error ? error : new Error(String(error)))
}

/** google places via our api, never the browser: suppliers behind the great
 *  firewall cannot reach google directly, but can always reach us.
 */
export function useAddressAutocomplete() {
  const [predictions, setPredictions] = useState<AddressPrediction[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlightRef = useRef<AbortController | null>(null)
  const sessionTokenRef = useRef<string>(crypto.randomUUID())

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      inFlightRef.current?.abort()
    }
  }, [])

  const search = useCallback((input: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!input.trim()) {
      inFlightRef.current?.abort()
      setPredictions([])
      return
    }

    setIsLoading(true)

    debounceRef.current = setTimeout(async () => {
      // the proxy adds a hop, so a slow earlier response must not land last
      inFlightRef.current?.abort()
      const controller = new AbortController()
      inFlightRef.current = controller
      try {
        const { data } = await api.get<{ predictions: AddressPrediction[] }>(
          '/v1/places/autocomplete',
          {
            params: { input, sessionToken: sessionTokenRef.current },
            signal: controller.signal
          }
        )
        setPredictions(data.predictions)
      } catch (error) {
        if (controller.signal.aborted) return
        if (!isAxiosError(error) || error.response?.status !== 400) {
          report(error)
        }
        setPredictions([])
      } finally {
        if (inFlightRef.current === controller) setIsLoading(false)
      }
    }, 300)
  }, [])

  /** rotating after a pick lets google bill us per session, not per request. */
  const rotateSession = useCallback(() => {
    sessionTokenRef.current = crypto.randomUUID()
  }, [])

  const getDetails = useCallback(
    async (
      placeId: string,
      description: string
    ): Promise<AddressPlaceDetails> => {
      try {
        const { data } = await api.get<PlaceDetailsResponse>(
          '/v1/places/details',
          { params: { placeId, sessionToken: sessionTokenRef.current } }
        )
        return { description, ...data, lookupFailed: false }
      } catch (error) {
        report(error)
        return {
          description,
          countryCode: null,
          country: null,
          region: null,
          latitude: null,
          longitude: null,
          lookupFailed: true
        }
      }
    },
    []
  )

  const clear = useCallback(() => {
    setPredictions([])
  }, [])

  return {
    predictions,
    isLoading,
    search,
    clear,
    rotateSession,
    getDetails
  }
}
